import { useState } from "react";
import RejectDialog from "./RejectDialog";
import ConfidenceField from "./ConfidenceField";
import type { EndorsementPlan } from "../../hooks/useWorkflowStatus";

interface SourceEmail {
  subject?: string;
  sender?: string;
}

interface Props {
  plan: EndorsementPlan;
  onApprove: (corrections: Record<string, unknown>) => void;
  onReject: (reason: string) => void;
  /**
   * Optional source-email banner — shown above Identification when present.
   * Ported from v1 PlanReviewView's source-email card so AMs can see
   * which email triggered the plan without leaving the drawer.
   */
  sourceEmail?: SourceEmail;
  /** Optional submit-busy indicators (drives button disabled state). */
  isApproving?: boolean;
  isRejecting?: boolean;
  /** Optional inline error string — bubbled from usePlanReview when present. */
  errorMessage?: string | null;
}

/**
 * Plan-review screen. Sections follow the ora idiom (see-possible-tile-shadow
 * with sunrise underlined headers); the editable fields are MUI TextFields
 * underneath, themed to use the Highstreet primary-gray + warning + error
 * tokens via `ConfidenceField`.
 */
export default function PlanReviewPanel({
  plan,
  onApprove,
  onReject,
  sourceEmail,
  isApproving = false,
  isRejecting = false,
  errorMessage = null,
}: Props) {
  const [corrections, setCorrections] = useState<Record<string, string>>({});
  const [rejectOpen, setRejectOpen] = useState(false);

  const handleFieldChange = (field: string, value: string) => {
    setCorrections((prev) => ({ ...prev, [field]: value }));
  };

  const handleApprove = () => {
    // Only send fields that were actually changed
    const changedFields: Record<string, unknown> = {};
    for (const [key, val] of Object.entries(corrections)) {
      const original = (plan as unknown as Record<string, unknown>)[key];
      if (
        original &&
        typeof original === "object" &&
        "value" in (original as Record<string, unknown>)
      ) {
        if ((original as Record<string, unknown>).value !== val) {
          changedFields[key] = val;
        }
      }
    }
    onApprove(changedFields);
  };

  return (
    <div className="flex flex-col gap-4">
      {/* Page heading */}
      <div>
        <h1 className="text-display-xs text-primary-gray-light-900">
          Review plan
        </h1>
        <p className="text-sm text-primary-gray-light-600 pt-[0.438rem]">
          Verify the parsed details below. Edit any field that needs
          correction, then approve or reject.
        </p>
      </div>

      {/* Source email — ported from v1 PlanReviewView source-email card. */}
      {sourceEmail?.subject && (
        <DetailSection title="Source email">
          <div className="text-sm text-primary-gray-light-700 font-bold">
            {sourceEmail.subject}
          </div>
          {sourceEmail.sender && (
            <div className="text-xs text-primary-gray-light-500">
              From: {sourceEmail.sender}
            </div>
          )}
        </DetailSection>
      )}

      {/* Inline error — surfaces ApiError messages above the action row. */}
      {errorMessage && (
        <div
          role="alert"
          className="rounded-lg border border-primary-error-200 bg-primary-error-25 px-3 py-2 text-sm text-primary-error-700"
        >
          {errorMessage}
        </div>
      )}

      {/* Identification — who + which policy */}
      <DetailSection title="Identification">
        <ConfidenceField
          label="Policy Number"
          field={plan.policy_number}
          onChange={(v) => handleFieldChange("policy_number", v)}
        />
        <ConfidenceField
          label="Client Name"
          field={plan.client_name}
          onChange={(v) => handleFieldChange("client_name", v)}
        />
        <ConfidenceField
          label="Carrier"
          field={plan.carrier}
          onChange={(v) => handleFieldChange("carrier", v)}
        />
      </DetailSection>

      {/* Change details */}
      <DetailSection title="Change">
        <ConfidenceField
          label="Line of Business"
          field={plan.line_of_business}
          onChange={(v) => handleFieldChange("line_of_business", v)}
        />
        <ConfidenceField
          label="Change Type"
          field={plan.change_type}
          onChange={(v) => handleFieldChange("change_type", v)}
        />
        <ConfidenceField
          label="Effective Date"
          field={plan.effective_date}
          onChange={(v) => handleFieldChange("effective_date", v)}
        />
        {plan.change_details && (
          <div className="text-sm text-primary-gray-light-600">
            <div className="font-bold mb-1">Details from email</div>
            <div className="whitespace-pre-wrap text-primary-gray-light-700">
              {plan.change_details}
            </div>
          </div>
        )}
      </DetailSection>

      {/* Context — only render when we have any of the fields */}
      {(plan.assigned_am ||
        plan.current_coverage_summary ||
        plan.epic_client_id) && (
        <DetailSection title="Context">
          {plan.assigned_am && (
            <Field label="Assigned AM" value={plan.assigned_am} />
          )}
          {plan.current_coverage_summary && (
            <Field
              label="Current coverage"
              value={plan.current_coverage_summary}
            />
          )}
          {plan.epic_client_id && (
            <Field label="EPIC client id" value={plan.epic_client_id} mono />
          )}
        </DetailSection>
      )}

      {/* Action row — Approve (primary) + Reject (error secondary) */}
      <div className="w-full flex justify-end gap-2 items-center">
        <button
          type="button"
          onClick={() => setRejectOpen(true)}
          disabled={isApproving || isRejecting}
          className="btn-error-secondary text-sm h-10 disabled:opacity-50"
        >
          {isRejecting ? "Rejecting…" : "Reject"}
        </button>
        <button
          type="button"
          onClick={handleApprove}
          disabled={isApproving || isRejecting}
          className="btn-primary text-sm h-10 px-4 disabled:opacity-50"
        >
          {isApproving ? "Approving…" : "Approve & Execute"}
        </button>
      </div>

      <RejectDialog
        open={rejectOpen}
        onClose={() => setRejectOpen(false)}
        onReject={onReject}
      />
    </div>
  );
}

function DetailSection({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section className="w-full flex flex-row see-possible-tile-shadow pb-4 px-4">
      <div className="w-full gap-[0.625rem] flex flex-col">
        <div className="w-full flex flex-row h-[2.375rem] justify-start text-primary-gray-light-900 text-xl border-b-[1px] !border-solid border-b-sunrise">
          <div className="mr-3 items-center flex">{title}</div>
        </div>
        <div className="w-full flex flex-col gap-2 pt-1">{children}</div>
      </div>
    </section>
  );
}

function Field({
  label,
  value,
  mono,
}: {
  label: string;
  value: string;
  mono?: boolean;
}) {
  return (
    <div className="text-sm text-primary-gray-light-600">
      <span className="font-bold">{label}: </span>
      {mono ? (
        <code className="text-primary-gray-light-700">{value}</code>
      ) : (
        <span className="text-primary-gray-light-700">{value}</span>
      )}
    </div>
  );
}
